import { useEffect } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/useAuth";
import { api } from "@/lib/api";
import { LayoutGrid, Link2, Inbox, Tags, Settings, LogOut, ExternalLink } from "lucide-react";
import LinksPage from "@/pages/dashboard/LinksPage";
import EmailsPage from "@/pages/dashboard/EmailsPage";
import CategoriesPage from "@/pages/dashboard/CategoriesPage";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import OverviewPage from "@/pages/dashboard/OverviewPage";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm">Loading…</div>
    );
  }

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/login", { replace: true });
  };

  const navItems = [
    { to: "/dashboard", label: "Overview", icon: LayoutGrid, end: true, testid: "nav-overview" },
    { to: "/dashboard/links", label: "Links", icon: Link2, testid: "nav-links" },
    { to: "/dashboard/emails", label: "Emails", icon: Inbox, testid: "nav-emails" },
    { to: "/dashboard/categories", label: "Categories", icon: Tags, testid: "nav-categories" },
    { to: "/dashboard/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950 font-body flex">
      <aside className="w-64 bg-white border-r border-zinc-200 flex flex-col shrink-0">
        <div className="px-6 py-6 border-b border-zinc-200">
          <div className="font-heading text-xl tracking-tight">Curator Hub</div>
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mt-1">Admin</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors duration-150 ${
                  isActive ? "bg-zinc-950 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-200 space-y-1">
          {user.handle ? (
            <a
              href={`/u/${user.handle}`}
              target="_blank"
              rel="noreferrer"
              data-testid="view-public-page"
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-100 rounded-sm"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View public page</span>
            </a>
          ) : null}
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-100 rounded-sm"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
          <div className="flex items-center gap-3 px-3 py-3 border-t border-zinc-200 mt-2">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-medium">
                {user.name?.[0] || "U"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-xs text-zinc-500 truncate">{user.email}</div>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Routes>
          <Route index element={<OverviewPage user={user} />} />
          <Route path="links" element={<LinksPage />} />
          <Route path="emails" element={<EmailsPage user={user} />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="settings" element={<SettingsPage user={user} />} />
        </Routes>
      </main>
    </div>
  );
}
